import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Plus, FolderOpen, Play, Edit, Trash2,
  Search, X, Clock, FileCode,
} from "lucide-react";
import { AppNav } from "@/components/AppNav";
import { useConfirm } from "@/components/ConfirmDialog";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function TestSuites() {
  const navigate = useNavigate();
  const [confirm, confirmDialog] = useConfirm();
  const [suites, setSuites] = useState([]);
  const [testCases, setTestCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedSuite, setSelectedSuite] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  
  // Form state
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    test_case_ids: [],
    is_shared_with_team: false,
    tags: []
  });
  const [tagInput, setTagInput] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [suitesRes, casesRes] = await Promise.all([
        axios.get(`${API}/suites`, { withCredentials: true }),
        axios.get(`${API}/test-cases`, { withCredentials: true })
      ]);
      setSuites(suitesRes.data);
      setTestCases(casesRes.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load test suites");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      test_case_ids: [],
      is_shared_with_team: false,
      tags: []
    });
    setTagInput("");
  };

  const handleCreateSuite = async () => {
    if (!formData.name.trim()) {
      toast.error("Suite name is required");
      return;
    }
    try {
      await axios.post(`${API}/suites`, formData, { withCredentials: true });
      toast.success("Test suite created!");
      setShowCreateModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error("Failed to create suite:", error);
      toast.error("Failed to create test suite");
    }
  };

  const handleUpdateSuite = async () => {
    if (!selectedSuite) return;
    try {
      await axios.put(`${API}/suites/${selectedSuite.id}`, formData, { withCredentials: true });
      toast.success("Test suite updated!");
      setShowEditModal(false);
      setSelectedSuite(null);
      resetForm();
      fetchData();
    } catch (error) {
      console.error("Failed to update suite:", error);
      toast.error("Failed to update test suite");
    }
  };

  const handleDeleteSuite = async (suiteId) => {
    const ok = await confirm({
      title: 'Delete this test suite?',
      description: 'The suite will be removed. The individual test cases inside it are not deleted.',
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/suites/${suiteId}`, { withCredentials: true });
      toast.success("Test suite deleted");
      fetchData();
    } catch (error) {
      console.error("Failed to delete suite:", error);
      toast.error("Failed to delete test suite");
    }
  };

  const handleRunSuite = async (suiteId) => {
    try {
      toast.info("Starting suite execution...");
      const response = await axios.post(`${API}/suites/${suiteId}/run`, {}, { withCredentials: true });
      toast.success(`Suite execution started! Running ${response.data.total_tests} tests.`);
      navigate(`/execution/${response.data.test_executions[0]}`);
    } catch (error) {
      console.error("Failed to run suite:", error);
      toast.error(error.response?.data?.detail || "Failed to run test suite");
    }
  };

  const openEditModal = (suite) => {
    setSelectedSuite(suite);
    setFormData({
      name: suite.name,
      description: suite.description || "",
      test_case_ids: suite.test_case_ids || [],
      is_shared_with_team: suite.is_shared_with_team || false,
      tags: suite.tags || []
    });
    setShowEditModal(true);
  };

  const toggleTestCaseSelection = (testCaseId) => {
    setFormData(prev => ({
      ...prev,
      test_case_ids: prev.test_case_ids.includes(testCaseId)
        ? prev.test_case_ids.filter(id => id !== testCaseId)
        : [...prev.test_case_ids, testCaseId]
    }));
  };

  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput("");
    }
  };

  const removeTag = (tag) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag)
    }));
  };

  const filteredSuites = suites.filter(suite =>
    suite.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    suite.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getTestCaseName = (testCaseId) => {
    const testCase = testCases.find(tc => tc.id === testCaseId);
    return testCase?.name || "Unknown Test";
  };

  return (
    <div className="min-h-screen bg-[#f8f9fb] text-slate-900" data-testid="test-suites-page">
      {confirmDialog}
      <div className="fixed inset-0 hero-glow" />
      <div className="fixed inset-0 opacity-5" 
        style={{
          backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"-%3E%3Cg fill="none" fill-rule="evenodd"-%3E%3Cg fill="%23ffffff" fill-opacity="0.4"-%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'
        }}
      />

      <div className="relative z-10">
        <AppNav />

        {/* Main Content */}
        <div className="px-6 py-12 md:px-12">
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-12">
              <div>
                <h1 className="text-4xl md:text-5xl font-extrabold tracking-tighter mb-4" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                  Test Suites
                </h1>
                <p className="text-slate-400">Organize and run test cases as collections</p>
              </div>
              <Button 
                data-testid="create-suite-btn"
                className="bg-[#00E599] text-black hover:bg-[#00CC88] rounded-sm font-semibold neon-glow"
                onClick={() => {
                  resetForm();
                  setShowCreateModal(true);
                }}
              >
                <Plus className="w-4 h-4 mr-2" />
                New Suite
              </Button>
            </div>

            {/* Search */}
            <div className="mb-8">
              <div className="relative w-full max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  data-testid="search-suites"
                  placeholder="Search suites..."
                  className="pl-10 pr-8 bg-[#0B0F1A] border-white/10 text-white rounded-sm"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-white"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Suites Grid */}
            {loading ? (
              <p className="text-slate-400">Loading...</p>
            ) : filteredSuites.length === 0 ? (
              <Card className="glass-card border-white/5 rounded-xl">
                <CardContent className="p-12 text-center">
                  <FolderOpen className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-2">No test suites yet</h3>
                  <p className="text-slate-400 mb-6">Create your first test suite to organize your tests</p>
                  <Button 
                    className="bg-[#00E599] text-black hover:bg-[#00CC88] rounded-sm font-semibold"
                    onClick={() => {
                      resetForm();
                      setShowCreateModal(true);
                    }}
                  >
                    Create Test Suite
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredSuites.map((suite, idx) => (
                  <motion.div
                    key={suite.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.05 }}
                  >
                    <Card 
                      className="glass-card border-white/5 rounded-xl hover:border-white/10 transition-colors"
                      data-testid={`suite-${suite.id}`}
                    >
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div className="w-12 h-12 bg-[#D946EF]/10 rounded-sm flex items-center justify-center mb-3">
                            <FolderOpen className="w-6 h-6 text-[#D946EF]" />
                          </div>
                          <Badge className="bg-slate-800 text-slate-300 border-slate-700">
                            {suite.test_case_ids?.length || 0} tests
                          </Badge>
                        </div>
                        <CardTitle className="text-lg" style={{ fontFamily: 'Unbounded, sans-serif' }}>{suite.name}</CardTitle>
                        <CardDescription className="text-slate-400 line-clamp-2">
                          {suite.description || "No description"}
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        {/* Tags */}
                        {suite.tags?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-4">
                            {suite.tags.slice(0, 3).map((tag, i) => (
                              <Badge key={i} variant="outline" className="text-xs border-[#00E599]/30 text-[#00E599]">
                                {tag}
                              </Badge>
                            ))}
                            {suite.tags.length > 3 && (
                              <Badge variant="outline" className="text-xs border-white/20 text-slate-400">
                                +{suite.tags.length - 3}
                              </Badge>
                            )}
                          </div>
                        )}

                        {/* Test Cases Preview */}
                        {suite.test_case_ids?.length > 0 && (
                          <div className="space-y-1 mb-4">
                            {suite.test_case_ids.slice(0, 2).map((tcId, i) => (
                              <div key={i} className="flex items-center gap-2 text-sm text-slate-400">
                                <FileCode className="w-3 h-3" />
                                <span className="truncate">{getTestCaseName(tcId)}</span>
                              </div>
                            ))}
                            {suite.test_case_ids.length > 2 && (
                              <p className="text-xs text-slate-500">+{suite.test_case_ids.length - 2} more tests</p>
                            )}
                          </div>
                        )}

                        {/* Actions */}
                        <div className="flex gap-2">
                          <Button 
                            data-testid={`run-suite-btn-${suite.id}`}
                            size="sm"
                            className="flex-1 bg-[#00E599] text-black hover:bg-[#00CC88] rounded-sm font-semibold"
                            onClick={() => handleRunSuite(suite.id)}
                            disabled={!suite.test_case_ids?.length}
                          >
                            <Play className="w-4 h-4 mr-1" />
                            Run
                          </Button>
                          <Button 
                            data-testid={`edit-suite-btn-${suite.id}`}
                            size="sm"
                            variant="ghost"
                            className="text-slate-400 hover:text-white hover:bg-white/5 rounded-sm"
                            onClick={() => openEditModal(suite)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button 
                            data-testid={`delete-suite-btn-${suite.id}`}
                            size="sm"
                            variant="ghost"
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-sm"
                            onClick={() => handleDeleteSuite(suite.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create Suite Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-2xl bg-[#0B0F1A] border-white/10 text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl" style={{ fontFamily: 'Unbounded, sans-serif' }}>
              Create Test Suite
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Group test cases into a collection that can be run together
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 mt-4">
            <div className="space-y-2">
              <Label>Suite Name *</Label>
              <Input
                data-testid="suite-name-input"
                placeholder="e.g., Login Flow Tests"
                className="bg-[#0B0F1A] border-white/10 text-white rounded-sm"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                data-testid="suite-description-input"
                placeholder="Describe what this suite tests..."
                className="bg-[#0B0F1A] border-white/10 text-white rounded-sm min-h-[80px]"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>

            {/* Tags */}
            <div className="space-y-2">
              <Label>Tags</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="Add a tag"
                  className="bg-[#0B0F1A] border-white/10 text-white rounded-sm"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                />
                <Button 
                  type="button"
                  variant="outline"
                  className="border-white/10 text-white hover:bg-white/5"
                  onClick={addTag}
                >
                  Add
                </Button>
              </div>
              {formData.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.tags.map((tag, i) => (
                    <Badge key={i} className="bg-[#00E599]/10 text-[#00E599] border-[#00E599]/30">
                      {tag}
                      <button onClick={() => removeTag(tag)} className="ml-1 hover:text-white">
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Share with team */}
            <div className="flex items-center justify-between">
              <div>
                <Label>Share with Team</Label>
                <p className="text-xs text-slate-500">Allow team members to view and run this suite</p>
              </div>
              <Switch
                checked={formData.is_shared_with_team}
                onCheckedChange={(checked) => setFormData({ ...formData, is_shared_with_team: checked })}
              />
            </div>

            {/* Test Cases Selection */}
            <div className="space-y-2">
              <Label>Select Test Cases</Label>
              <ScrollArea className="h-[200px] border border-white/10 rounded-sm p-2">
                {testCases.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">No test cases available</p>
                ) : (
                  <div className="space-y-2">
                    {testCases.map((tc) => (
                      <div
                        key={tc.id}
                        className={`flex items-center gap-3 p-3 rounded-sm cursor-pointer transition-colors ${
                          formData.test_case_ids.includes(tc.id)
                            ? 'bg-[#00E599]/10 border border-[#00E599]/30'
                            : 'bg-white/5 hover:bg-white/10'
                        }`}
                        onClick={() => toggleTestCaseSelection(tc.id)}
                      >
                        <Checkbox 
                          checked={formData.test_case_ids.includes(tc.id)}
                          className="data-[state=checked]:bg-[#00E599] data-[state=checked]:border-[#00E599]"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{tc.name}</p>
                          <p className="text-xs text-slate-500">{tc.steps?.length || 0} steps</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
              <p className="text-xs text-slate-500">
                {formData.test_case_ids.length} test case(s) selected
              </p>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                className="flex-1 border-white/10 text-white hover:bg-white/5"
                onClick={() => setShowCreateModal(false)}
              >
                Cancel
              </Button>
              <Button
                data-testid="create-suite-submit-btn"
                className="flex-1 bg-[#00E599] text-black hover:bg-[#00CC88] rounded-sm font-semibold"
                onClick={handleCreateSuite}
              >
                Create Suite
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Suite Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-2xl bg-[#0B0F1A] border-white/10 text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl" style={{ fontFamily: 'Unbounded, sans-serif' }}>
              Edit Test Suite
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Update the suite configuration
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 mt-4">
            <div className="space-y-2">
              <Label>Suite Name *</Label>
              <Input
                data-testid="edit-suite-name-input"
                placeholder="e.g., Login Flow Tests"
                className="bg-[#0B0F1A] border-white/10 text-white rounded-sm"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                data-testid="edit-suite-description-input"
                placeholder="Describe what this suite tests..."
                className="bg-[#0B0F1A] border-white/10 text-white rounded-sm min-h-[80px]"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>

            {/* Tags */}
            <div className="space-y-2">
              <Label>Tags</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="Add a tag"
                  className="bg-[#0B0F1A] border-white/10 text-white rounded-sm"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                />
                <Button 
                  type="button"
                  variant="outline"
                  className="border-white/10 text-white hover:bg-white/5"
                  onClick={addTag}
                >
                  Add
                </Button>
              </div>
              {formData.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.tags.map((tag, i) => (
                    <Badge key={i} className="bg-[#00E599]/10 text-[#00E599] border-[#00E599]/30">
                      {tag}
                      <button onClick={() => removeTag(tag)} className="ml-1 hover:text-white">
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Share with team */}
            <div className="flex items-center justify-between">
              <div>
                <Label>Share with Team</Label>
                <p className="text-xs text-slate-500">Allow team members to view and run this suite</p>
              </div>
              <Switch
                checked={formData.is_shared_with_team}
                onCheckedChange={(checked) => setFormData({ ...formData, is_shared_with_team: checked })}
              />
            </div>

            {/* Test Cases Selection */}
            <div className="space-y-2">
              <Label>Select Test Cases</Label>
              <ScrollArea className="h-[200px] border border-white/10 rounded-sm p-2">
                {testCases.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">No test cases available</p>
                ) : (
                  <div className="space-y-2">
                    {testCases.map((tc) => (
                      <div
                        key={tc.id}
                        className={`flex items-center gap-3 p-3 rounded-sm cursor-pointer transition-colors ${
                          formData.test_case_ids.includes(tc.id)
                            ? 'bg-[#00E599]/10 border border-[#00E599]/30'
                            : 'bg-white/5 hover:bg-white/10'
                        }`}
                        onClick={() => toggleTestCaseSelection(tc.id)}
                      >
                        <Checkbox 
                          checked={formData.test_case_ids.includes(tc.id)}
                          className="data-[state=checked]:bg-[#00E599] data-[state=checked]:border-[#00E599]"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{tc.name}</p>
                          <p className="text-xs text-slate-500">{tc.steps?.length || 0} steps</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
              <p className="text-xs text-slate-500">
                {formData.test_case_ids.length} test case(s) selected
              </p>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                className="flex-1 border-white/10 text-white hover:bg-white/5"
                onClick={() => setShowEditModal(false)}
              >
                Cancel
              </Button>
              <Button
                data-testid="edit-suite-submit-btn"
                className="flex-1 bg-[#00E599] text-black hover:bg-[#00CC88] rounded-sm font-semibold"
                onClick={handleUpdateSuite}
              >
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
